// Classic invoice layout using @react-pdf/renderer
// Receives a single canonical InvoiceDisplayModel — no tax math inside this component.
import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import { fmtInr, numberToWords } from "./invoiceUtils";

const ClassicLayout = ({ model }) => {
  const {
    invoice_number,
    invoice_date,
    due_date,
    status,
    notes,
    terms,
    copyType,
    items,
    subtotal,
    discountPercent,
    discountAmount,
    cgstPercent,
    cgstAmount,
    sgstPercent,
    sgstAmount,
    igstPercent,
    igstAmount,
    shipping,
    roundoff,
    total,
    organization,
    customer,
    customerShipped,
    template,
  } = model || {};

  const t = template || {};
  const primary = t.primary_color || "#002FA7";
  const secondary = t.secondary_color || "#0A0A0B";
  
  // Get item columns from template, default to classic columns
  const itemColumns = Array.isArray(t.item_columns) 
    ? t.item_columns 
    : ["item_name", "description", "hsn_sac", "quantity", "unit", "price", "amount"];

  // Use shipped customer if provided, otherwise bill-to customer
  const shipToCustomer = customerShipped || customer;

  const styles = StyleSheet.create({
    page: {
      padding: 40,
      fontFamily: "Helvetica",
      fontSize: 10,
    },
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 20,
      borderBottomWidth: 2,
      borderBottomColor: secondary,
      paddingBottom: 16,
    },
    companySection: {
      flex: 1,
    },
    companyName: {
      fontSize: 20,
      fontWeight: "bold",
      color: primary,
      marginBottom: 4,
    },
    companyDescription: {
      fontSize: 12,
      color: "#666",
      marginBottom: 8,
    },
    companyInfo: {
      fontSize: 10,
      color: "#666",
      lineHeight: 1.5,
    },
    invoiceTitleSection: {
      textAlign: "right",
    },
    invoiceTitle: {
      fontSize: 18,
      fontWeight: "bold",
      color: primary,
      marginBottom: 4,
    },
    cancelled: {
      fontSize: 20,
      fontWeight: "bold",
      color: "#dc2626",
      marginTop: 8,
    },
    invoiceDetails: {
      fontSize: 10,
      color: "#666",
      marginTop: 8,
    },
    sectionTitle: {
      fontSize: 12,
      fontWeight: "bold",
      color: primary,
      marginBottom: 8,
    },
    customerInfo: {
      fontSize: 10,
      color: "#333",
      lineHeight: 1.5,
      marginBottom: 20,
    },
    customerName: {
      fontWeight: "bold",
    },
    table: {
      width: "100%",
      marginBottom: 20,
    },
    tableHeader: {
      flexDirection: "row",
      backgroundColor: secondary,
      color: "#fff",
    },
    tableRow: {
      flexDirection: "row",
      borderBottomWidth: 1,
      borderBottomColor: "#ddd",
    },
    tableCell: {
      padding: 8,
      fontSize: 9,
    },
    itemName: {
      fontWeight: "bold",
    },
    hsn: {
      fontSize: 8,
      color: "#666",
    },
    textRight: {
      textAlign: "right",
    },
    textCenter: {
      textAlign: "center",
    },
    summary: {
      alignSelf: "flex-end",
      width: 250,
    },
    summaryRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      padding: "8 0",
      borderBottomWidth: 1,
      borderBottomColor: "#eee",
    },
    summaryTotal: {
      flexDirection: "row",
      justifyContent: "space-between",
      padding: 12,
      backgroundColor: primary,
      color: "#fff",
      marginTop: 8,
      fontWeight: "bold",
    },
    amountWords: {
      marginTop: 20,
      padding: 16,
      backgroundColor: "#f9f9f9",
    },
    amountWordsTitle: {
      fontSize: 10,
      fontWeight: "bold",
      color: "#666",
      marginBottom: 4,
    },
    amountWordsText: {
      fontSize: 10,
      color: "#333",
      fontStyle: "italic",
    },
    termsNotes: {
      marginTop: 20,
    },
    footer: {
      marginTop: 32,
      paddingTop: 16,
      borderTopWidth: 1,
      borderTopColor: "#eee",
      textAlign: "center",
      fontSize: 9,
      color: "#666",
    },
    signature: {
      marginTop: 32,
      textAlign: "right",
    },
    signatureLine: {
      borderTopWidth: 2,
      borderTopColor: "#000",
      paddingTop: 8,
      minWidth: 150,
      display: "inline-block",
    },
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.companySection}>
            <Text style={styles.companyName}>
              {organization?.organization_name || "Your Business"}
            </Text>
            {organization?.description && (
              <Text style={styles.companyDescription}>
                {organization.description}
              </Text>
            )}
            <View style={styles.companyInfo}>
              {organization?.address && <Text>{organization.address}</Text>}
              <Text>
                {[organization?.city, organization?.state, organization?.pincode]
                  .filter(Boolean)
                  .join(", ")}
              </Text>
              {organization?.email && (
                <Text>Email: {organization.email}</Text>
              )}
              {organization?.phone && (
                <Text>Phone: {organization.phone}</Text>
              )}
            </View>
          </View>
          <View style={styles.invoiceTitleSection}>
            <Text style={styles.invoiceTitle}>
              {t.invoice_title || "INVOICE"}
            </Text>
            {status === "cancelled" && (
              <Text style={styles.cancelled}>CANCELLED</Text>
            )}
            <View style={styles.invoiceDetails}>
              <Text>Invoice No: {invoice_number}</Text>
              <Text>Date: {invoice_date}</Text>
              {due_date && <Text>Due Date: {due_date}</Text>}
              <Text>
                Copy Type:{" "}
                {copyType.charAt(0).toUpperCase() + copyType.slice(1)}
              </Text>
            </View>
          </View>
        </View>

        {/* Bill To */}
        <View>
          <Text style={styles.sectionTitle}>Bill To:</Text>
          <View style={styles.customerInfo}>
            <Text style={styles.customerName}>{customer?.name || "-"}</Text>
            {customer?.company && <Text>{customer.company}</Text>}
            {customer?.address && <Text>{customer.address}</Text>}
            <Text>
              {[customer?.city, customer?.state, customer?.country]
                .filter(Boolean)
                .join(", ")}
            </Text>
            {customer?.email && <Text>Email: {customer.email}</Text>}
            {customer?.phone && <Text>Phone: {customer.phone}</Text>}
          </View>
        </View>

        {/* Ship To */}
        {shipToCustomer && shipToCustomer !== customer && (
          <View>
            <Text style={styles.sectionTitle}>Ship To:</Text>
            <View style={styles.customerInfo}>
              <Text style={styles.customerName}>
                {shipToCustomer.name || "-"}
              </Text>
              {shipToCustomer.company && (
                <Text>{shipToCustomer.company}</Text>
              )}
              {shipToCustomer.address && (
                <Text>{shipToCustomer.address}</Text>
              )}
              <Text>
                {[
                  shipToCustomer.city,
                  shipToCustomer.state,
                  shipToCustomer.country,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </Text>
              {shipToCustomer.email && (
                <Text>Email: {shipToCustomer.email}</Text>
              )}
              {shipToCustomer.phone && (
                <Text>Phone: {shipToCustomer.phone}</Text>
              )}
            </View>
          </View>
        )}

        {/* Items Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text
              style={[styles.tableCell, styles.textCenter, { flex: 0.5 }]}
            >
              Sr No.
            </Text>
            {itemColumns.map((col) => {
              const columnConfig = {
                item_name: { label: "Item", flex: 3, align: "left" },
                description: { label: "Description", flex: 2, align: "left" },
                hsn_sac: { label: "HSN/SAC", flex: 1, align: "center" },
                quantity: { label: "Qty", flex: 0.5, align: "center" },
                unit: { label: "Unit", flex: 0.5, align: "center" },
                price: { label: "Price", flex: 1, align: "right" },
                amount: { label: "Amount", flex: 1, align: "right" },
              };
              const config = columnConfig[col] || { label: col, flex: 1, align: "left" };
              return (
                <Text
                  key={col}
                  style={[
                    styles.tableCell,
                    config.align === "center" && styles.textCenter,
                    config.align === "right" && styles.textRight,
                    { flex: config.flex }
                  ]}
                >
                  {config.label}
                </Text>
              );
            })}
          </View>
          {(items || []).map((item, idx) => (
            <View key={item.id || idx} style={styles.tableRow}>
              <Text
                style={[
                  styles.tableCell,
                  styles.textCenter,
                  { flex: 0.5 },
                ]}
              >
                {idx + 1}
              </Text>
              {itemColumns.map((col) => {
                const columnConfig = {
                  item_name: { flex: 3, align: "left" },
                  description: { flex: 2, align: "left" },
                  hsn_sac: { flex: 1, align: "center" },
                  quantity: { flex: 0.5, align: "center" },
                  unit: { flex: 0.5, align: "center" },
                  price: { flex: 1, align: "right" },
                  amount: { flex: 1, align: "right" },
                };
                const config = columnConfig[col] || { flex: 1, align: "left" };
                
                // Special handling for item_name column to show HSN below it
                if (col === "item_name") {
                  return (
                    <View key={col} style={[styles.tableCell, { flex: config.flex }]}>
                      <Text style={styles.itemName}>{item.item_name}</Text>
                      {item.hsn_sac && !itemColumns.includes("hsn_sac") && (
                        <Text style={styles.hsn}>HSN: {item.hsn_sac}</Text>
                      )}
                    </View>
                  );
                }
                
                const value = col === "price" ? fmtInr(item.price) 
                  : col === "amount" ? fmtInr(item.amount)
                  : item[col] || "-";
                return (
                  <Text
                    key={col}
                    style={[
                      styles.tableCell,
                      config.align === "center" && styles.textCenter,
                      config.align === "right" && styles.textRight,
                      { flex: config.flex }
                    ]}
                  >
                    {value}
                  </Text>
                );
              })}
            </View>
          ))}
        </View>

        {/* Summary */}
        <View style={styles.summary}>
          <View style={styles.summaryRow}>
            <Text>Subtotal:</Text>
            <Text>{fmtInr(subtotal)}</Text>
          </View>
          {discountAmount > 0 && (
            <View style={styles.summaryRow}>
              <Text>Discount ({discountPercent}%):</Text>
              <Text>-{fmtInr(discountAmount)}</Text>
            </View>
          )}
          {cgstAmount > 0 && (
            <View style={styles.summaryRow}>
              <Text>CGST ({cgstPercent}%):</Text>
              <Text>{fmtInr(cgstAmount)}</Text>
            </View>
          )}
          {sgstAmount > 0 && (
            <View style={styles.summaryRow}>
              <Text>SGST ({sgstPercent}%):</Text>
              <Text>{fmtInr(sgstAmount)}</Text>
            </View>
          )}
          {igstAmount > 0 && (
            <View style={styles.summaryRow}>
              <Text>IGST ({igstPercent}%):</Text>
              <Text>{fmtInr(igstAmount)}</Text>
            </View>
          )}
          {shipping > 0 && (
            <View style={styles.summaryRow}>
              <Text>Shipping:</Text>
              <Text>{fmtInr(shipping)}</Text>
            </View>
          )}
          {roundoff !== 0 && Math.abs(roundoff) > 0.001 && (
            <View style={styles.summaryRow}>
              <Text>Round Off:</Text>
              <Text>{fmtInr(roundoff)}</Text>
            </View>
          )}
          <View style={styles.summaryTotal}>
            <Text>Total:</Text>
            <Text>{fmtInr(total)}</Text>
          </View>
        </View>

        {/* Amount in Words */}
        <View style={styles.amountWords}>
          <Text style={styles.amountWordsTitle}>Amount in Words:</Text>
          <Text style={styles.amountWordsText}>
            {numberToWords(total || 0)}
          </Text>
        </View>

        {/* Terms */}
        {t.show_terms !== false && terms && (
          <View style={styles.termsNotes}>
            <Text style={styles.sectionTitle}>Terms & Conditions:</Text>
            <Text style={{ fontSize: 9, lineHeight: 1.5 }}>{terms}</Text>
          </View>
        )}

        {/* Notes */}
        {t.show_notes !== false && notes && (
          <View style={styles.termsNotes}>
            <Text style={styles.sectionTitle}>Notes:</Text>
            <Text style={{ fontSize: 9, lineHeight: 1.5 }}>{notes}</Text>
          </View>
        )}

        {/* Signature */}
        {t.show_signature !== false && (
          <View style={styles.signature}>
            {organization?.signature && (
              <Image
                src={organization.signature}
                style={{ maxHeight: 60, marginBottom: 8 }}
              />
            )}
            <View style={styles.signatureLine}>
              <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                Authorized Signatory
              </Text>
            </View>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          {t.footer_text && <Text>{t.footer_text}</Text>}
          {t.show_powered_by !== false && <Text>Powered by BillForge</Text>}
        </View>
      </Page>
    </Document>
  );
};

ClassicLayout.displayName = "ClassicLayout";

export default ClassicLayout;
